import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { useIntercomDispatcher } from '@/hooks/comm/use-comm';
import { type RespondedType, type ResponseError, responseScheme } from '@/models/response';
import { useAuthStore } from '@/stores/auth';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { queryClient } from '@/utils/query-client';
import { avatarUrlSchema, nameSchema } from '@/utils/schemas';

export default function useSignUp() {
  const { dispatch } = useIntercomDispatcher();

  const mutation = useMutation<RespondedType & SignUpResponse, ResponseError, SignUpRequest>({
    mutationKey: ['/PhoneRegister'],
    mutationFn: (params) =>
      fetcher('/PhoneRegister', params, {
        requireAuth: false,
        schema: responseScheme.merge(signUpResponseSchema),
        validResponseCodes: [0],
      }),
    retry: false,
    onSuccess: (response) => {
      if (response.Code === 0 && response.UID) {
        useAuthStore.getState().start({
          UserID: response.UID,
          NickName: response.NickName,
          AvatarUrl: response.HeadImg,
          Token: response.Token || response.AccessToken || '',
          RefreshToken: response.RefreshToken || '',
        });
        dispatch({ event: 'update-auth-credentials' });
      }
    },
    onSettled: (data, error) => {
      addToast({
        type: error ? 'error' : 'success',
        message: error?.message ?? data?.Msg,
      });
    },
  });

  const signUp = async (input: SignUpRequest) => mutation.mutateAsync(input).then((res) => res.Code === 0);

  return {
    signUp,
    ...mutation,
  };
}

const signUpRequestSchema = z.object({
  Phone: z.string().regex(/^\d+$/),
  TelCode: z.string(),
  Code: z.string().min(6),
});

type SignUpRequest = z.infer<typeof signUpRequestSchema>;

const signUpResponseSchema = z.object({
  UID: z.number().nullish(),
  Token: z.string().nullish(),
  AccessToken: z.string().nullish(),
  RefreshToken: z.string().nullish(),
  NickName: nameSchema,
  HeadImg: avatarUrlSchema,
});

type SignUpResponse = z.infer<typeof signUpResponseSchema>;
